import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

function RequestRow({ r, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/deletion-requests/${r.request_id}/done`, { note });
      toast.success("Marked as done");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setSaving(false);
    }
  };
  const open = r.status === "open";
  return (
    <Card className="p-5 rounded-2xl border-slate-200 bg-white" data-testid={`deletion-request-${r.request_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-lg font-semibold truncate">{r.name || "Unknown contact"}</span>
            <Badge className={open ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"} data-testid={`deletion-status-${r.request_id}`}>
              {open ? "to action" : "done"}
            </Badge>
          </div>
          <div className="text-sm text-slate-600 mt-1">WhatsApp +{r.phone}{r.email ? ` · ${r.email}` : ""}</div>
          <div className="text-xs text-slate-500 mt-1">
            Requested {new Date(r.created_at).toLocaleString("en-IN")} · {r.user_id ? `Account ${r.user_id}` : "No linked account"}
          </div>
        </div>
      </div>
      {open ? (
        <div className="mt-4 space-y-2">
          <Textarea placeholder="What did you delete or keep? (required)" value={note} onChange={e => setNote(e.target.value)} rows={2} className="rounded-xl" data-testid={`deletion-note-${r.request_id}`} />
          <Button size="sm" disabled={!note.trim() || saving} onClick={submit} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid={`deletion-done-${r.request_id}`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark as done
          </Button>
        </div>
      ) : (
        <div className="mt-3 text-sm text-slate-600" data-testid={`deletion-done-note-${r.request_id}`}>
          Done {r.completed_at ? new Date(r.completed_at).toLocaleDateString("en-IN") : ""}{r.completed_by ? ` by ${r.completed_by}` : ""}: {r.note}
        </div>
      )}
    </Card>
  );
}

export function DeletionRequests({ onCount }) {
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/deletion-requests");
      setItems(data);
      onCount?.(data.filter(r => r.status === "open").length);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load requests");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (items.length === 0) {
    return <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="deletion-empty">No data deletion requests yet.</div>;
  }
  return <div className="space-y-4" data-testid="deletion-list">{items.map(r => <RequestRow key={r.request_id} r={r} onDone={load} />)}</div>;
}
